import React, {useEffect, useState} from 'react'
import {useSearchParams, useLocation, useNavigate} from 'react-router-dom'
import * as api from '@api'
import {USER_PREFERENCE_KEY_ENUMS} from '@constants'
import {useAuth} from '@state/auth'
import ErrorToast from '@components/ErrorToast'

import useToast from '@/hooks/useToast'

export default function useAnalyticsFilterPreferences({
  savedFilter = null,
  analyticsFilterPreferenceKey,
  savePreferences = true,
}) {
  const {toast} = useToast()
  const {me, meReceived, mePreferencesUpdated} = useAuth(state => ({
    me: state.me,
    meReceived: state.meReceived,
    mePreferencesUpdated: state.mePreferencesUpdated,
  }))
  const [searchParams, setSearchParams] = useSearchParams()
  const [filterStateRetrieved, setFilterStateRetrieved] = useState(false)
  const userSetFilter = searchParams.get('filter')
  const location = useLocation()
  const navigate = useNavigate()

  let filterValue = JSON.parse(searchParams.get('filter')) || {}

  const getSavedAnalyticsFilterPreference = key => {
    let savedPreference =
      me.preferences?.[key] && Object.keys(me.preferences[key]).length > 0 ? JSON.stringify(me.preferences[key]) : null
    savedPreference = savedPreference == null ? [] : JSON.parse(savedPreference)
    savedPreference = savedPreference.filter(c => c.report == analyticsFilterPreferenceKey)
    return savedPreference
  }

  const updatePreferencesInDb = () => {
    api
      .mePreferencesPatch([
        {
          key: USER_PREFERENCE_KEY_ENUMS.ANALYTICS_FILTER_PREFERENCES,
          value: me.preferences[USER_PREFERENCE_KEY_ENUMS.ANALYTICS_FILTER_PREFERENCES],
        },
      ])
      .then(response => {
        if (response.status >= 400) {
          toast.custom(t => <ErrorToast t={t} error={response.error} />)
        } else {
          meReceived({...me, preferences: response.data, mePreferencesUpdated: false})
        }
      })
  }

  useEffect(() => {
    const intervalId = setInterval(() => {
      if (mePreferencesUpdated) {
        updatePreferencesInDb()
      }
    }, 5000)

    return () => clearInterval(intervalId)
  }, [mePreferencesUpdated])

  useEffect(() => {
    if (!savePreferences) return

    savedFilter = getSavedAnalyticsFilterPreference(USER_PREFERENCE_KEY_ENUMS.ANALYTICS_FILTER_PREFERENCES)
    savedFilter = savedFilter?.length > 0 ? savedFilter[0].filter || [] : []

    if (savedFilter) {
      let filterVal = {}
      for (const [key, value] of Object.entries(savedFilter)) {
        if (value && value.length) {
          filterVal = {...filterVal, [key]: value}
        }
        if (typeof value === 'object' && !Array.isArray(value) && value !== null) {
          filterVal[key] = value.key || ''
        }
      }
      filterValue = filterVal
      if (Object.keys(filterValue).length > 0) {
        searchParams.set('filter', JSON.stringify(filterValue))
      }
    }

    //if user has set filter, then use merge savedFilter with userSetFilter
    if (userSetFilter) {
      let userFilter = JSON.parse(userSetFilter)
      for (const [key, value] of Object.entries(userFilter)) {
        if (value === '') delete userFilter[key]
      }
      if (Object.keys(userFilter).length > 0) {
        filterValue = {...userFilter, ...filterValue}
        searchParams.set('filter', JSON.stringify(filterValue))
      }
    }

    setFilterStateRetrieved(true)

    const url = `${location.pathname}?${searchParams.toString()}`
    navigate(url, {replace: true})
  }, [])

  const handleSaveAnalyticsFilterPreferences = filter => {
    if (!analyticsFilterPreferenceKey) {
      toast.error('Analytics filter preferences key missing')
      return
    }

    let analyticsPreferencesKey = USER_PREFERENCE_KEY_ENUMS.ANALYTICS_FILTER_PREFERENCES
    let analyticsFilterPreferences =
      JSON.parse(
        me.preferences?.[analyticsPreferencesKey] && Object.keys(me.preferences[analyticsPreferencesKey]).length > 0
          ? JSON.stringify(me.preferences[analyticsPreferencesKey])
          : null
      ) || []

    let filterPreferenceIndex = analyticsFilterPreferences.findIndex(t => t.report == analyticsFilterPreferenceKey)
    if (filterPreferenceIndex > -1) {
      analyticsFilterPreferences[filterPreferenceIndex] = {
        ...analyticsFilterPreferences[filterPreferenceIndex],
        filter,
      }
    } else {
      let preference = {
        report: analyticsFilterPreferenceKey,
        filter,
      }
      analyticsFilterPreferences.push(preference)
    }

    meReceived({
      ...me,
      preferences: {...me.preferences, [analyticsPreferencesKey]: analyticsFilterPreferences},
      mePreferencesUpdated: true,
    })
  }

  return {
    handleSaveAnalyticsFilterPreferences,
    filterValue,
    getSavedAnalyticsFilterPreference,
    searchParams,
    setSearchParams,
    filterStateRetrieved,
  }
}
